import { Dispatch, ReactNode, RefObject, SetStateAction, useEffect } from "react";

import useModalHandler from "../../hooks/useModalHandler.hook";
import CloseIcon from "../../icons/iconClose.comp";

interface ModalProps {
  id?: string;
  setModalOpened: Dispatch<SetStateAction<boolean>>;
  btnRef: RefObject<HTMLElement>;
  children: ReactNode;
  cssHeight?: string;
}

export default function Modal({
  id = "overlay",
  setModalOpened,
  btnRef,
  children,
  cssHeight = "top-[20dvh] md:top-1/2 h-[60dvh] md:h-[400px] ",
}: ModalProps) {
  const modalRef = useModalHandler(btnRef, setModalOpened);

  const handleOverLayClick = (e) => {
    console.log("handleOverLayClick", e.target);

    const isModal = (modalRef.current as any).contains(e.target);
    if (!isModal) {
      setModalOpened(false);
    }
  };

  const modalClassName =
    "fixed top-0 right-0 bottom-0 left-0 " +
    "md:right-1/2 md:-translate-y-1/2 md:translate-x-1/2 md:left-auto md:bottom-auto " +
    "bg-white dark:bg-her-darkSectionBg border-2 border-dark-gray-text " +
    "w-full md:w-[760px] " +
    cssHeight +
    "rounded-2xl";

  return (
    <article
      id={id}
      className="z-[100] fixed top-0 left-0  left right-0 bottom-0  bg-gray-500 bg-opacity-40"
      onClick={handleOverLayClick}
    >
      <div
        ref={modalRef}
        className={modalClassName}
        onClick={(e) => {
          e.stopPropagation();
        }}
      >
        <button
          id="modal-close-btn"
          onClick={(e) => {
            e.stopPropagation();
            console.log("close modal");
            setModalOpened(false);
          }}
          type="button"
          className="absolute w-10 h-10 z-10 rounded-full top-2 right-2 flex justify-center items-center hover:scale-[110%] hover:font-bold"
        >
          <CloseIcon className="w-8 h-8" />
        </button>
        {children}
      </div>
    </article>
  );
}

export function PosCartModal({ setModalOpened, btnRef, children }: ModalProps) {
  const modalRef = useModalHandler(btnRef, setModalOpened);

  const handleOverLayClick = (e) => {
    const isModal = (modalRef.current as any).contains(e.target);

    if (!isModal) {
      setModalOpened(false);
    }
  };

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "Escape") {
        setModalOpened(false);
      }
    };

    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [setModalOpened]);

  const modalClassName =
    "fixed top-1/2 right-1/2 bottom-0 left-0 min-w-[350px] min-h-[350px] " +
    "py-12 px-6 bg-white rounded-xl shadow-sm" +
    "top-1/2 right-1/2 -translate-y-1/2 translate-x-1/2 left-auto bottom-auto " +
    "md:w-auto md:h-auto md:min-w-[400px] md:min-h-[400px] ";

  return (
    <main
      id="overlay"
      className="fixed top-0 left-0 left right-0 bottom-0 bg-gray-500 bg-opacity-40 "
      onClick={handleOverLayClick}
    >
      <div ref={modalRef} className={modalClassName}>
        <button id="modal-close-btn" onClick={() => setModalOpened(false)} className="absolute top-0 -right-[60px]">
          {escapeBtnIcon}
        </button>

        {children}
      </div>
    </main>
  );
}

const escapeBtnIcon = (
  <svg width="48" height="71" viewBox="0 0 48 71" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect y="0.5" width="48" height="48" rx="24" fill="white" />
    <path d="M31 17.5L17 31.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M17 17.5L31 31.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    <path
      d="M14.968 57.928V60.82H18.184V61.66H14.968V64.612H18.544V65.5H13.876V57.04H18.544V57.928H14.968ZM25.0583 63.304C25.0583 63.704 24.9623 64.076 24.7703 64.42C24.5783 64.764 24.2863 65.044 23.8943 65.26C23.5023 65.476 23.0183 65.584 22.4423 65.584C21.8823 65.584 21.3823 65.484 20.9423 65.284C20.5103 65.076 20.1623 64.796 19.8983 64.444C19.6423 64.084 19.4943 63.676 19.4543 63.22H20.6183C20.6423 63.46 20.7223 63.696 20.8583 63.928C21.0023 64.152 21.2023 64.34 21.4583 64.492C21.7223 64.636 22.0383 64.708 22.4063 64.708C22.8863 64.708 23.2543 64.58 23.5103 64.324C23.7663 64.068 23.8943 63.74 23.8943 63.34C23.8943 62.996 23.8023 62.716 23.6183 62.5C23.4423 62.284 23.2223 62.116 22.9583 61.996C22.6943 61.868 22.3343 61.732 21.8783 61.588C21.3423 61.412 20.9103 61.248 20.5823 61.096C20.2623 60.936 19.9863 60.7 19.7543 60.388C19.5303 60.076 19.4183 59.664 19.4183 59.152C19.4183 58.712 19.5223 58.324 19.7303 57.988C19.9463 57.652 20.2503 57.392 20.6423 57.208C21.0423 57.024 21.5103 56.932 22.0463 56.932C22.8783 56.932 23.5383 57.136 24.0263 57.544C24.5143 57.944 24.7983 58.456 24.8783 59.08H23.7023C23.6383 58.744 23.4663 58.448 23.1863 58.192C22.9063 57.936 22.5263 57.808 22.0463 57.808C21.6063 57.808 21.2543 57.924 20.9903 58.156C20.7263 58.38 20.5943 58.7 20.5943 59.116C20.5943 59.444 20.6783 59.712 20.8463 59.92C21.0223 60.12 21.2383 60.28 21.4943 60.4C21.7503 60.512 22.1023 60.64 22.5503 60.784C23.0943 60.96 23.5303 61.128 23.8583 61.288C24.1943 61.448 24.4783 61.692 24.7103 62.02C24.9423 62.34 25.0583 62.768 25.0583 63.304ZM30.0306 56.956C31.0626 56.956 31.9146 57.212 32.5866 57.724C33.2586 58.228 33.7226 58.92 33.9786 59.8H32.8266C32.6106 59.24 32.2626 58.796 31.7826 58.468C31.3026 58.132 30.7146 57.964 30.0186 57.964C29.4666 57.964 28.9706 58.1 28.5306 58.372C28.0986 58.636 27.7546 59.02 27.4986 59.524C27.2506 60.02 27.1266 60.604 27.1266 61.276C27.1266 61.94 27.2506 62.52 27.4986 63.016C27.7546 63.512 28.0986 63.896 28.5306 64.168C28.9706 64.432 29.4666 64.564 30.0186 64.564C30.7146 64.564 31.3026 64.4 31.7826 64.072C32.2626 63.736 32.6106 63.288 32.8266 62.728H33.9786C33.7226 63.608 33.2586 64.304 32.5866 64.816C31.9146 65.32 31.0626 65.572 30.0306 65.572C29.2386 65.572 28.5386 65.392 27.9306 65.032C27.3226 64.664 26.8466 64.156 26.5026 63.508C26.1666 62.852 25.9986 62.108 25.9986 61.276C25.9986 60.436 26.1666 59.688 26.5026 59.032C26.8466 58.376 27.3226 57.868 27.9306 57.508C28.5386 57.14 29.2386 56.956 30.0306 56.956Z"
      fill="white"
    />
  </svg>
);
